import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip, ListBox, Spinner, Typography } from '@heroui/react';
import { EmptyState } from '@heroui-pro/react';
import { Icon } from '@iconify/react';
import type { RosterUser } from '@/api/models';
import { getNodeTestIdProp, getNodeTestProps } from '@/utils';
import {
    getInitials,
    getRoleLabelKey,
    ROLE_META,
} from '@/pages/sellerLogin/sellerLoginMeta';

type ShiftApproverPickerProps = {
    approvers: RosterUser[];
    approversLoading: boolean;
    approversError: boolean;
    /** The approver to focus when the picker appears, resolved by the hook. */
    focusApproverId: string | null;
    selectApprover: (user: RosterUser) => void;
};

const PICKER_HEADING_ID = 'seller-shift-approver-heading';

/**
 * The co-sign's approver picker (render-only): branch admins (already
 * filtered by the hook to admins who aren't the current seller) as a
 * design-system `ListBox` in grid layout — the login roster's tile pattern
 * (bos-seller#61). One Tab stop; arrows, typeahead and `Enter`/click/tap are
 * the list box's own, all firing `selectApprover` through `onAction`. It takes
 * focus as soon as it has admins to show, from each tile's ref callback for
 * the same reason `SellerRosterStep` does: `ListBox` builds its collection in
 * a follow-up render, so no tile exists yet when the picker's own effects run.
 */
export const ShiftApproverPicker: React.FC<ShiftApproverPickerProps> = ({
    approvers,
    approversLoading,
    approversError,
    focusApproverId,
    selectApprover,
}) => {
    const { t } = useTranslation('pages');

    const hasFocusedRef = React.useRef(false);

    const focusOnMount = (userId: string) => (node: HTMLDivElement | null) => {
        if (node && !hasFocusedRef.current && userId === focusApproverId) {
            node.focus();
            hasFocusedRef.current = true;
        }
    };

    return (
        <div
            className="flex flex-col gap-4"
            {...getNodeTestProps('seller-shift-approver-picker')}
        >
            <Typography color="muted" id={PICKER_HEADING_ID} type="body-sm">
                {t('seller.shift.selectApprover')}
            </Typography>

            {approversLoading ? (
                <div
                    className="flex min-h-40 items-center justify-center"
                    {...getNodeTestProps('seller-shift-approvers-loading')}
                >
                    <Spinner aria-label={t('seller.shift.loadingApprovers')} />
                </div>
            ) : approversError ? (
                <div
                    className="flex min-h-40 items-center justify-center"
                    {...getNodeTestProps('seller-shift-approvers-error')}
                >
                    <EmptyState>
                        <EmptyState.Header>
                            <EmptyState.Media variant="icon">
                                <Icon icon="solar:danger-triangle-bold-duotone" />
                            </EmptyState.Media>
                            <EmptyState.Title>
                                {t('seller.shift.approversError')}
                            </EmptyState.Title>
                        </EmptyState.Header>
                    </EmptyState>
                </div>
            ) : approvers.length === 0 ? (
                <div
                    className="flex min-h-40 items-center justify-center"
                    {...getNodeTestProps('seller-shift-approvers-empty')}
                >
                    <EmptyState>
                        <EmptyState.Header>
                            <EmptyState.Media variant="icon">
                                <Icon icon="solar:shield-cross-bold-duotone" />
                            </EmptyState.Media>
                            <EmptyState.Title>
                                {t('seller.shift.noApprovers')}
                            </EmptyState.Title>
                        </EmptyState.Header>
                    </EmptyState>
                </div>
            ) : (
                <ListBox
                    aria-labelledby={PICKER_HEADING_ID}
                    className="grid grid-cols-2 gap-3"
                    layout="grid"
                    onAction={(key) => {
                        const user = approvers.find((item) => item.id === key);
                        if (user) {
                            selectApprover(user);
                        }
                    }}
                    selectionMode="none"
                    {...getNodeTestIdProp('seller-shift-approver-grid')}
                >
                    {approvers.map((user) => {
                        const meta = ROLE_META[user.role];
                        return (
                            <ListBox.Item
                                className="flex-col items-center gap-3 rounded-2xl border border-border bg-surface p-4 text-center hover:border-accent/40 hover:bg-surface-secondary"
                                id={user.id}
                                key={user.id}
                                ref={focusOnMount(user.id)}
                                textValue={user.name}
                                {...getNodeTestIdProp(
                                    `seller-shift-approver-${user.id}`,
                                )}
                            >
                                <Avatar size="lg">
                                    <Avatar.Fallback>
                                        {getInitials(user.name)}
                                    </Avatar.Fallback>
                                </Avatar>
                                <Typography
                                    className="line-clamp-1"
                                    slot="label"
                                    weight="semibold"
                                >
                                    {user.name}
                                </Typography>
                                <Chip
                                    color={meta.color}
                                    size="sm"
                                    variant="soft"
                                >
                                    <Icon
                                        className="size-3.5"
                                        icon={meta.icon}
                                    />
                                    {t(getRoleLabelKey(user.role))}
                                </Chip>
                            </ListBox.Item>
                        );
                    })}
                </ListBox>
            )}
        </div>
    );
};
